import { STATES } from "./geo";
import { POLLS_AS_OF, RACE_POLLS } from "./polls";
import { ISSUES, NEIGHBOR_SHARES, TURNOUT } from "./survey";
import type {
  Call,
  Component,
  ContextStats,
  CountyEstimate,
  Crosstab,
  District,
  DistrictProjection,
  ExternalRow,
  ForesightEstimate,
  Layer,
  ModelSettings,
  Office,
  Party,
  PollBlend,
  Race,
  RaceForecast,
  RacePoll,
  RecallCode,
  RecallCrosstab,
  Respondent,
  SeatProjection,
  StateInfo,
  StateSample,
  Strength,
} from "./types";

/* ── Constants ─────────────────────────────────── */

export const DEFAULT_SETTINGS: ModelSettings = {
  externalWeight: 0.5,
  modelError: 5,
  ownWeight: 0.75,
};

/** Spread assumed when an external source gives a margin but no odds. */
export const EXTERNAL_SIGMA = 6;

/** Margin points toward a governor running for re-election. */
export const GOVERNOR_INCUMBENCY = 6;

/** Part of a governor race's distance from the national environment it
    keeps: the simulated respondents split tickets less than real voters,
    and governor races are where real voters split them most. */
export const GOVERNOR_PULL = 0.8;

/** Part of a `discounted` independent's own-vote lead over their state's
    2024 result that they keep. */
export const INDEPENDENT_KEEP = 0.5;

/** Most of a state's likely-voter weight 2024 non-voters may carry:
    validated-voter studies put them at 3–8% of a midterm electorate. */
export const NON_VOTER_SHARE = 0.06;

/** Own-vote weights offered in the settings; each one is published. */
export const OWN_WEIGHTS = [0, 0.25, 0.5, 0.75, 1];

/** Most of a race's read its public polls can make up. */
export const POLL_MAX_WEIGHT = 0.6;

/** Days before POLLS_AS_OF in which a poll must have ended to count. */
export const POLL_WINDOW_DAYS = 30;

/** Pseudo-respondents pulling a county toward its expected value. */
const COUNTY_SHRINKAGE = 8;
/** Candidate and incumbency effects, which a generic ballot can't see
    (margin points). */
const CANDIDATE_ERROR = 3.5;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Spread of true district swings around their state's from local
    trends, which the district's own respondents can narrow (margin pts). */
const DISTRICT_ERROR = 5;
/** Respondents a district needs before its own swing weighs as much as
    the state's: binomial variance over the spread of district swings. */
const DISTRICT_PRIOR_N = 0.25 / (DISTRICT_ERROR / 200) ** 2;
const HOUSE_MAJORITY = 218;
const HOUSE_SIMULATIONS = 5000;
/** Margin points a `discounted` independent's poll average moves toward
    their opponent: such candidates have run 1–2 points behind their polls
    beyond each year's polling error, and red-state polls have tended to
    miss Republicans. */
const INDEPENDENT_POLL_FADE = 3;
const MIN_CALIBRATION = 20;
const NATIONAL_ERROR = 3;
/** Error of a race's poll average (margin points). */
const POLL_ERROR = 5;
const SIMULATIONS = 20000;
/** Extra spread for a state read from the surveyed states' average. */
const UNSURVEYED_ERROR = 4;

/** Left-side win odds implied by a Cook rating, used only as a prior. */
const COOK_PRIOR: Record<string, number> = {
  "likely d": 0.88,
  "likely r": 0.12,
  "lean d": 0.7,
  "lean r": 0.3,
  "safe d": 0.98,
  "safe r": 0.02,
  "solid d": 0.98,
  "solid r": 0.02,
  "toss-up": 0.5,
  tossup: 0.5,
};

const RECALL_CROSSTABS: { label: string; recall: RecallCode }[] = [
  { label: "Trump voters", recall: "T" },
  { label: "Harris voters", recall: "H" },
  { label: "2024 non-voters", recall: "N" },
];

/* ── Math ──────────────────────────────────────── */

const clamp = (x: number, min: number, max: number) =>
  Math.min(max, Math.max(min, x));

/** Standard normal CDF (Abramowitz–Stegun 7.1.26). */
export const normalCdf = (x: number): number => {
  const z = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * z);
  const poly =
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) *
      t +
      0.254829592) *
    t;
  const erf = 1 - poly * Math.exp(-z * z);
  return x >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
};

/** Inverse standard normal CDF (Acklam). */
export const normalQuantile = (probability: number): number => {
  const p = clamp(probability, 1e-9, 1 - 1e-9);
  const a = [
    -39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269,
    -30.66479806614716, 2.506628277459239,
  ];
  const b = [
    -54.47609879822406, 161.5858368580409, -155.6989798598866,
    66.80131188771972, -13.28068155288572,
  ];
  const c = [
    -0.007784894002430293, -0.3223964580411365, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
  ];
  const d = [
    0.007784695709041462, 0.3224671290700398, 2.445134137142996,
    3.754408661907416,
  ];
  const tail = (q: number) =>
    (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
    ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  if (p < 0.02425) return tail(Math.sqrt(-2 * Math.log(p)));
  if (p > 0.97575) return -tail(Math.sqrt(-2 * Math.log(1 - p)));
  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) *
      q) /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
};

/** Deterministic PRNG so seat odds don't jitter between renders. */
const mulberry32 = (seed: number) => {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const gaussian = (random: () => number) => {
  const u = Math.max(random(), 1e-12);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * random());
};

/* ── 2024 baselines ────────────────────────────── */

/** Trump's share of the 2024 two-party vote, 0–1. */
export const trumpTwoParty = (state: StateInfo): number =>
  state.pres2024.trump / (state.pres2024.trump + state.pres2024.harris);

/** County FIPS → Harris share of the 2024 two-party vote. */
export type County2024 = Record<string, number>;

/* ── Weights ───────────────────────────────────── */

interface Calibration {
  /** Sample's recalled Trump share minus the actual result, in points. */
  bias: number | null;
  factorH: number;
  factorT: number;
}

/**
 * National turnout edge δ: Harris voters weigh ×δ and Trump voters ×1/δ in
 * every state, δ set so the national House own vote (surveyed states
 * weighted by their 2024 vote) is the same after the non-voter cap and the
 * edge as without them. 1 before any House read.
 */
export const turnoutEdge = (samples: Record<string, StateSample>): number => {
  const reads = STATES.flatMap((state) => {
    const sample = samples[state.abbr];
    if (state.abbr === "DC" || !sample || !sample.offices.includes("house")) {
      return [];
    }
    const { respondents } = sample;
    const calibration = recallCalibration(respondents, state);
    return [{ calibration, respondents, state }];
  });
  const national = (edge: number, capped: boolean): number | null => {
    let sum = 0;
    let total = 0;
    for (const { calibration, respondents, state } of reads) {
      const weights = voterWeight(respondents, calibration, edge);
      const weight = capped ? weights.weight : weights.calibrated;
      const own = twoParty(tallyVotes(respondents, "house", weight));
      if (!own) continue;
      sum += own.leftShare * state.pres2024.total;
      total += state.pres2024.total;
    }
    return total > 0 ? sum / total : null;
  };

  const target = national(1, false);
  if (target === null) return 1;
  let low = 0.5;
  let high = 2;
  while (high - low > 1e-4) {
    const edge = (low + high) / 2;
    const read = national(edge, true);
    if (read !== null && read < target) low = edge;
    else high = edge;
  }
  return (low + high) / 2;
};

const turnoutWeight = (r: Respondent): number =>
  r.turnout === null ? 0.5 : (TURNOUT[r.turnout]?.weight ?? 0);

/**
 * Recalled-vote calibration: reweights Trump and Harris 2024 voters so the
 * sample's recalled 2024 vote matches the certified state result. It
 * corrects a sample that leans one way (a known risk with LLM respondents)
 * while keeping 2026 switchers and new voters intact.
 */
const recallCalibration = (
  respondents: Respondent[],
  state: StateInfo
): Calibration => {
  let trump = 0;
  let harris = 0;
  for (const r of respondents) {
    if (r.recall === "T") trump++;
    if (r.recall === "H") harris++;
  }
  if (trump + harris < MIN_CALIBRATION || trump === 0 || harris === 0) {
    return { bias: null, factorH: 1, factorT: 1 };
  }
  const actual = trumpTwoParty(state);
  const sample = trump / (trump + harris);
  return {
    bias: (sample - actual) * 100,
    factorH: clamp((1 - actual) / (1 - sample), 1 / 3, 3),
    factorT: clamp(actual / sample, 1 / 3, 3),
  };
};

const recallFactor = (
  r: Respondent,
  calibration: Calibration,
  edge: number
): number =>
  r.recall === "T"
    ? calibration.factorT / edge
    : r.recall === "H"
      ? calibration.factorH * edge
      : 1;

/**
 * How far respondents misjudge their surroundings: the mean gap between the
 * 2024 vote they attribute to their neighbors and the actual 2024 result
 * where they live (county when known, else state). Subtracted from the
 * 2026 neighbor read.
 */
const neighborBias = (
  respondents: Respondent[],
  state: StateInfo,
  county2024: County2024 | null
): number | null => {
  const stateHarris = 1 - trumpTwoParty(state);
  let sum = 0;
  let n = 0;
  for (const r of respondents) {
    if (r.neighbors2024 === null) continue;
    const perceived = NEIGHBOR_SHARES[r.neighbors2024];
    if (perceived === undefined) continue;
    const local = r.county ? county2024?.[r.county] : undefined;
    sum += perceived - (local ?? stateHarris);
    n++;
  }
  return n >= MIN_CALIBRATION ? sum / n : null;
};

/** Didn't vote in 2024, or was too young to. */
const isNonVoter = (r: Respondent): boolean =>
  r.recall === "N" || r.recall === "X";

/** Likely-voter weight: stated turnout × recall calibration × turnout
    edge (`calibrated`), then non-voters scaled down to NON_VOTER_SHARE of
    it, never up (`weight`). */
const voterWeight = (
  respondents: Respondent[],
  calibration: Calibration,
  edge: number
) => {
  const calibrated = (r: Respondent) =>
    turnoutWeight(r) * recallFactor(r, calibration, edge);
  let total = 0;
  let nonVoters = 0;
  for (const r of respondents) {
    const w = calibrated(r);
    total += w;
    if (isNonVoter(r)) nonVoters += w;
  }
  const share = total > 0 ? nonVoters / total : 0;
  const cap =
    share > NON_VOTER_SHARE
      ? (NON_VOTER_SHARE * (1 - share)) / (share * (1 - NON_VOTER_SHARE))
      : 1;
  return {
    calibrated,
    nonVoterShare: share,
    weight: (r: Respondent) => calibrated(r) * (isNonVoter(r) ? cap : 1),
  };
};

/* ── Estimators ────────────────────────────────── */

interface Tally {
  left: number;
  n: number;
  other: number;
  right: number;
  sumSquares: number;
  sumWeights: number;
  undecided: number;
}

const tallyVotes = (
  respondents: Respondent[],
  office: Office,
  weight: (r: Respondent) => number
): Tally => {
  const tally: Tally = {
    left: 0,
    n: 0,
    other: 0,
    right: 0,
    sumSquares: 0,
    sumWeights: 0,
    undecided: 0,
  };
  for (const r of respondents) {
    const vote = r.offices[office]?.vote;
    if (!vote || vote === "N") continue;
    const w = weight(r);
    if (w <= 0) continue;
    if (vote === "O") tally.other += w;
    if (vote === "U") tally.undecided += w;
    if (vote === "L" || vote === "R") {
      if (vote === "L") tally.left += w;
      else tally.right += w;
      tally.n++;
      tally.sumSquares += w * w;
      tally.sumWeights += w;
    }
  }
  return tally;
};

const twoParty = (tally: Tally): Component | null =>
  tally.left + tally.right > 0
    ? { leftShare: tally.left / (tally.left + tally.right), n: tally.n }
    : null;

const neighborComponent = (
  respondents: Respondent[],
  office: Office,
  bias: number | null
): Component | null => {
  let sum = 0;
  let n = 0;
  for (const r of respondents) {
    const bucket = r.offices[office]?.neighbors;
    if (bucket === null || bucket === undefined) continue;
    const share = NEIGHBOR_SHARES[bucket];
    if (share === undefined) continue;
    sum += share;
    n++;
  }
  if (n === 0) return null;
  return { leftShare: clamp(sum / n - (bias ?? 0), 0.01, 0.99), n };
};

/** The blend with a race's adjustments: the independent discount moves
    the own read; the incumbency bonus, governor pull and polls the
    result. */
const adjustedBlend = (
  own: Component | null,
  neighbors: Component | null,
  ownWeight: number,
  adjustments: Pick<
    ForesightEstimate,
    "governorPull" | "incumbencyBonus" | "independentDiscount" | "polls"
  >
): number | null => {
  const discount = (adjustments.independentDiscount ?? 0) / 200;
  const share = blend(
    own && { ...own, leftShare: clamp(own.leftShare + discount, 0, 1) },
    neighbors,
    ownWeight
  );
  const shift =
    (adjustments.incumbencyBonus ?? 0) +
    (adjustments.governorPull ?? 0) +
    (adjustments.polls?.shift ?? 0);
  return share === null ? null : clamp(share + shift / 200, 0, 1);
};

const blend = (
  own: Component | null,
  neighbors: Component | null,
  ownWeight: number
): number | null => {
  if (own && neighbors) {
    return ownWeight * own.leftShare + (1 - ownWeight) * neighbors.leftShare;
  }
  if (own) return own.leftShare;
  if (neighbors) return neighbors.leftShare;
  return null;
};

/** Moves a governor race's margin so it keeps GOVERNOR_PULL of its
    distance from the national environment (margin points). */
const governorPull = (
  race: Race | null,
  margin: number,
  environment: number | null
): number | null =>
  race?.office === "governor" && environment !== null
    ? (1 - GOVERNOR_PULL) * (environment - margin)
    : null;

const incumbencyBonus = (race: Race): number | null => {
  if (race.office !== "governor" || !race.incumbentRunning) return null;
  if (race.left?.name === race.incumbent) return GOVERNOR_INCUMBENCY;
  if (race.right?.name === race.incumbent) return -GOVERNOR_INCUMBENCY;
  return null;
};

/** Pulls the own read toward the state's 2024 result so a `discounted`
    candidate keeps INDEPENDENT_KEEP of their lead over it (margin points). */
const independentDiscount = (
  race: Race,
  own: Component,
  state: StateInfo
): number | null => {
  if (!race.left?.discounted && !race.right?.discounted) return null;
  const base = 1 - trumpTwoParty(state);
  return (base - own.leftShare) * (1 - INDEPENDENT_KEEP) * 200;
};

/** Blends a race's public polls into its margin and spread: they weigh
    n / (n + 2), a third for one poll and half for two, up to
    POLL_MAX_WEIGHT, after a `discounted` independent's average moves
    INDEPENDENT_POLL_FADE toward their opponent. The variances average by
    the same weights, as in the ensemble layer: the respondents and the
    polls share errors. */
const pollBlend = (
  race: Race,
  margin: number,
  sigma: number
): { polls: PollBlend; sigma: number } | null => {
  const average = pollAverage(
    RACE_POLLS.find(
      (r) =>
        r.state === race.state &&
        r.office === race.office &&
        r.special === race.special
    )?.polls ?? []
  );
  if (!average) return null;
  const fade = race.left?.discounted
    ? -INDEPENDENT_POLL_FADE
    : race.right?.discounted
      ? INDEPENDENT_POLL_FADE
      : 0;
  const weight = Math.min(POLL_MAX_WEIGHT, average.n / (average.n + 2));
  return {
    polls: {
      ...average,
      fade,
      shift: weight * (average.margin + fade - margin),
      weight,
    },
    sigma: Math.sqrt((1 - weight) * sigma ** 2 + weight * POLL_ERROR ** 2),
  };
};

export interface EstimateContext {
  county2024: County2024 | null;
  /** National turnout edge, from `turnoutEdge`. */
  edge: number;
  /** Generic ballot governor races are pulled toward, from
      `nationalEnvironment`; null before any House read. */
  environment: number | null;
  settings: ModelSettings;
  state: StateInfo;
}

const weightsFor = (sample: StateSample, context: EstimateContext) => {
  const calibration = recallCalibration(sample.respondents, context.state);
  return {
    ...voterWeight(sample.respondents, calibration, context.edge),
    bias: neighborBias(sample.respondents, context.state, context.county2024),
    calibration,
  };
};

/** Foresight's read of one office in one state, with the race's
    adjustments and public polls (none for the House, read without a
    race). */
export const estimateOffice = (
  sample: StateSample,
  office: Office,
  context: EstimateContext,
  race: Race | null
): ForesightEstimate | null => {
  const { respondents } = sample;
  const { bias, calibration, nonVoterShare, weight } = weightsFor(
    sample,
    context
  );

  const rawTally = tallyVotes(respondents, office, turnoutWeight);
  const tally = tallyVotes(respondents, office, weight);
  const own = twoParty(tally);
  const neighbors = neighborComponent(respondents, office, bias);
  const adjustments = {
    incumbencyBonus: race ? incumbencyBonus(race) : null,
    independentDiscount:
      race && own ? independentDiscount(race, own, context.state) : null,
  };
  const unpulled = adjustedBlend(own, neighbors, context.settings.ownWeight, {
    ...adjustments,
    governorPull: null,
    polls: null,
  });
  if (unpulled === null) return null;
  const pull = governorPull(
    race,
    (2 * unpulled - 1) * 100,
    context.environment
  );
  const read = clamp(unpulled + (pull ?? 0) / 200, 0, 1);

  let expectLeft = 0;
  let expectRight = 0;
  for (const r of respondents) {
    const expect = r.offices[office]?.expect;
    if (expect === "L") expectLeft++;
    if (expect === "R") expectRight++;
  }

  const effective =
    tally.sumSquares > 0
      ? (tally.sumWeights * tally.sumWeights) / tally.sumSquares
      : (neighbors?.n ?? 0);
  const p = own?.leftShare ?? read;
  const sampling =
    effective > 0 ? 200 * Math.sqrt((p * (1 - p)) / effective) : 10;
  const readSigma = Math.sqrt(sampling ** 2 + context.settings.modelError ** 2);
  const blended = race
    ? pollBlend(race, (2 * read - 1) * 100, readSigma)
    : null;
  const leftShare = clamp(read + (blended?.polls.shift ?? 0) / 200, 0, 1);
  const margin = (2 * leftShare - 1) * 100;
  const sigma = blended?.sigma ?? readSigma;
  const decided =
    rawTally.left + rawTally.right + rawTally.other + rawTally.undecided;

  return {
    calibrationBias: calibration.bias,
    expectLeft:
      expectLeft + expectRight > 0
        ? expectLeft / (expectLeft + expectRight)
        : null,
    governorPull: pull,
    ...adjustments,
    leftProbability: normalCdf(margin / sigma),
    leftShare,
    margin,
    neighbors,
    nonVoterShare,
    own,
    ownRaw: twoParty(rawTally),
    polls: blended?.polls ?? null,
    sampleSize: respondents.length,
    sigma,
    undecided: decided > 0 ? rawTally.undecided / decided : 0,
  };
};

/**
 * County reads, shrunk in proportion to sample toward the county's 2024
 * result moved by the state's swing (toward the state read when 2024 is
 * unknown), so a thin county keeps its lean instead of reading as a swing.
 */
export const estimateCounties = (
  sample: StateSample,
  office: Office,
  context: EstimateContext,
  estimate: ForesightEstimate
): CountyEstimate[] => {
  const { bias, weight } = weightsFor(sample, context);
  const stateSwing = estimate.leftShare - (1 - trumpTwoParty(context.state));
  const byCounty = new Map<string, Respondent[]>();
  for (const r of sample.respondents) {
    if (!r.county) continue;
    const list = byCounty.get(r.county) ?? [];
    list.push(r);
    byCounty.set(r.county, list);
  }

  return [...byCounty.entries()].map(([fips, respondents]) => {
    const raw = adjustedBlend(
      twoParty(tallyVotes(respondents, office, weight)),
      neighborComponent(respondents, office, bias),
      context.settings.ownWeight,
      estimate
    );
    const past = context.county2024?.[fips];
    const prior =
      past === undefined
        ? estimate.leftShare
        : clamp(past + stateSwing, 0.01, 0.99);
    const n = respondents.length;
    return {
      fips,
      leftShare:
        raw === null
          ? prior
          : (n * raw + COUNTY_SHRINKAGE * prior) / (n + COUNTY_SHRINKAGE),
      n,
      name: sample.countyNames[fips] ?? fips,
      raw,
    };
  });
};

const CROSSTABS: {
  group: string;
  label: string;
  test: (r: Respondent) => boolean;
}[] = [
  { group: "Age", label: "18–29", test: (r) => r.age !== null && r.age < 30 },
  {
    group: "Age",
    label: "30–44",
    test: (r) => r.age !== null && r.age >= 30 && r.age < 45,
  },
  {
    group: "Age",
    label: "45–64",
    test: (r) => r.age !== null && r.age >= 45 && r.age < 65,
  },
  { group: "Age", label: "65+", test: (r) => r.age !== null && r.age >= 65 },
  { group: "Gender", label: "Women", test: (r) => r.gender === "female" },
  { group: "Gender", label: "Men", test: (r) => r.gender === "male" },
  {
    group: "Education",
    label: "College grad",
    test: (r) => r.college === true,
  },
  { group: "Education", label: "No degree", test: (r) => r.college === false },
  { group: "Area", label: "Urban", test: (r) => r.area === "urban" },
  { group: "Area", label: "Suburban", test: (r) => r.area === "suburban" },
  { group: "Area", label: "Rural", test: (r) => r.area === "rural" },
];

/** Own-vote two-party share by demographic, likely-voter weighted. */
export const crosstabs = (
  sample: StateSample,
  office: Office,
  context: EstimateContext
): Crosstab[] => {
  const { weight } = weightsFor(sample, context);
  return CROSSTABS.map((c) => {
    const tally = tallyVotes(sample.respondents.filter(c.test), office, weight);
    return {
      group: c.group,
      label: c.label,
      leftShare: twoParty(tally)?.leftShare ?? null,
      n: tally.n,
    };
  });
};

/** Own vote by recalled 2024 vote, likely-voter weighted; respondents who
    won't vote in the race are left out. */
export const recallCrosstabs = (
  sample: StateSample,
  office: Office,
  context: EstimateContext
): RecallCrosstab[] => {
  const { weight } = weightsFor(sample, context);
  return RECALL_CROSSTABS.map(({ label, recall }) => {
    const sums = { L: 0, O: 0, R: 0, U: 0 };
    let n = 0;
    for (const r of sample.respondents) {
      const vote = r.offices[office]?.vote;
      if (r.recall !== recall || !vote || vote === "N") continue;
      const w = weight(r);
      if (w <= 0) continue;
      sums[vote] += w;
      n++;
    }
    const total = sums.L + sums.O + sums.R + sums.U;
    const share = (x: number) => (total > 0 ? x / total : 0);
    return {
      label,
      left: share(sums.L),
      n,
      other: share(sums.O),
      right: share(sums.R),
      undecided: share(sums.U),
    };
  });
};

/** Likely-voter context: approval, issues, finances, early vote. */
export const contextStats = (
  sample: StateSample,
  context: EstimateContext
): ContextStats => {
  const { weight } = weightsFor(sample, context);
  let total = 0;
  let alreadyVoted = 0;
  let approvalTotal = 0;
  let approve = 0;
  let disapprove = 0;
  let financeTotal = 0;
  let issueTotal = 0;
  const finances = [0, 0, 0];
  const issues = ISSUES.map(() => 0);

  for (const r of sample.respondents) {
    const w = weight(r);
    if (w <= 0) continue;
    total += w;
    if (r.turnout === 0) alreadyVoted += w;
    if (r.approval !== null) approvalTotal += w;
    if (r.approval === 0 || r.approval === 1) approve += w;
    if (r.approval === 2 || r.approval === 3) disapprove += w;
    if (r.finances !== null && r.finances < finances.length) {
      finances[r.finances] += w;
      financeTotal += w;
    }
    if (r.issue !== null && r.issue < issues.length) {
      issues[r.issue] += w;
      issueTotal += w;
    }
  }

  const share = (x: number, of: number) => (of > 0 ? x / of : 0);
  return {
    alreadyVoted: share(alreadyVoted, total),
    approve: approvalTotal > 0 ? share(approve, total) : null,
    disapprove: approvalTotal > 0 ? share(disapprove, total) : null,
    finances:
      financeTotal > 0 ? finances.map((x) => share(x, financeTotal)) : null,
    issues: issueTotal > 0 ? issues.map((x) => share(x, issueTotal)) : null,
  };
};

/** The environment governor races are pulled toward: the national generic
    ballot of the surveyed states' House reads, which aren't pulled
    themselves (margin points). Null before any House read. */
export const nationalEnvironment = (
  samples: Record<string, StateSample>,
  context: Omit<EstimateContext, "environment" | "state">
): number | null => {
  const reads = STATES.flatMap((state) => {
    const sample = samples[state.abbr];
    if (state.abbr === "DC" || !sample || !sample.offices.includes("house")) {
      return [];
    }
    const estimate = estimateOffice(
      sample,
      "house",
      { ...context, environment: null, state },
      null
    );
    return estimate ? [{ estimate, state }] : [];
  });
  return nationalGeneric(reads, STATES)?.margin ?? null;
};

/** National generic ballot: state reads weighted by 2024 turnout. */
export const nationalGeneric = (
  reads: { estimate: ForesightEstimate; state: StateInfo }[],
  allStates: StateInfo[]
): { coverage: number; leftShare: number; margin: number } | null => {
  if (reads.length === 0) return null;
  let weighted = 0;
  let weights = 0;
  for (const { estimate, state } of reads) {
    weighted += estimate.leftShare * state.pres2024.total;
    weights += state.pres2024.total;
  }
  const nationalTotal = allStates.reduce((s, x) => s + x.pres2024.total, 0);
  const leftShare = weighted / weights;
  return {
    coverage: weights / nationalTotal,
    leftShare,
    margin: (2 * leftShare - 1) * 100,
  };
};

/** The average two-party margin (left minus right, points) of the polls
    that ended in the POLL_WINDOW_DAYS before POLLS_AS_OF, and how many
    they are; null without any. */
export const pollAverage = (
  polls: RacePoll[]
): Pick<PollBlend, "margin" | "n"> | null => {
  const asOf = Date.parse(POLLS_AS_OF);
  const margins = polls.flatMap((poll) => {
    const age = (asOf - Date.parse(poll.end)) / DAY_MS;
    return age >= 0 && age <= POLL_WINDOW_DAYS
      ? [((poll.left - poll.right) / (poll.left + poll.right)) * 100]
      : [];
  });
  if (margins.length === 0) return null;
  return {
    margin: margins.reduce((sum, m) => sum + m, 0) / margins.length,
    n: margins.length,
  };
};

/* ── Layers & calls ────────────────────────────── */

export const externalProbability = (row: ExternalRow): number | null =>
  row.leftProbability ??
  (row.margin !== null ? normalCdf(row.margin / EXTERNAL_SIGMA) : null);

const cookPrior = (race: Race): number | null =>
  race.cook ? (COOK_PRIOR[race.cook.trim().toLowerCase()] ?? null) : null;

/** An external row as a forecast (margin-only rows get odds from it). */
export const externalForecast = (
  external: ExternalRow | null
): RaceForecast | null => {
  const probability = external ? externalProbability(external) : null;
  return external && probability !== null
    ? {
        leftProbability: probability,
        margin: external.margin,
        sigma: EXTERNAL_SIGMA,
        source: "external",
      }
    : null;
};

/** What a layer shows from the Foresight and external reads of a race. */
export const layerForecast = (
  layer: Exclude<Layer, "difference">,
  fs: RaceForecast | null,
  ext: RaceForecast | null,
  settings: ModelSettings
): RaceForecast | null => {
  if (layer === "foresight") return fs;
  if (layer === "external") return ext;
  if (!fs || !ext) return fs ?? ext;
  const w = settings.externalWeight;
  return {
    leftProbability: w * ext.leftProbability + (1 - w) * fs.leftProbability,
    margin:
      ext.margin !== null && fs.margin !== null
        ? w * ext.margin + (1 - w) * fs.margin
        : (fs.margin ?? ext.margin),
    sigma: Math.sqrt(w * ext.sigma ** 2 + (1 - w) * fs.sigma ** 2),
    source: "ensemble",
  };
};

/** A race's read under a layer; `prior` falls back to the Cook rating. */
export const forecastFor = (
  layer: Exclude<Layer, "difference">,
  race: Race | null,
  foresight: ForesightEstimate | null,
  external: ExternalRow | null,
  settings: ModelSettings,
  allowPrior: boolean
): RaceForecast | null => {
  const fs: RaceForecast | null = foresight
    ? {
        leftProbability: foresight.leftProbability,
        margin: foresight.margin,
        sigma: foresight.sigma,
        source: "foresight",
      }
    : null;
  const read = layerForecast(layer, fs, externalForecast(external), settings);
  if (read || !allowPrior || !race) return read;

  const prior = cookPrior(race);
  return prior === null
    ? null
    : {
        leftProbability: prior,
        margin: null,
        sigma: EXTERNAL_SIGMA,
        source: "prior",
      };
};

export const strengthFor = (leftProbability: number): Strength => {
  const lead = Math.max(leftProbability, 1 - leftProbability);
  if (lead < 0.6) return "tossup";
  if (lead < 0.8) return "lean";
  if (lead < 0.95) return "likely";
  return "safe";
};

export const callFor = (
  forecast: RaceForecast | null,
  leftParty: Party,
  rightParty: Party = "R"
): Call => {
  if (!forecast) {
    return {
      leader: null,
      leftProbability: null,
      margin: null,
      strength: "tossup",
    };
  }
  const p = forecast.leftProbability;
  return {
    leader: p > 0.5 ? leftParty : p < 0.5 ? rightParty : null,
    leftProbability: p,
    margin: forecast.margin,
    strength: strengthFor(p),
  };
};

/** True when the projected leader differs from the party holding the seat. */
export const isFlip = (call: Call, race: Race): boolean =>
  call.leader !== null && call.leader !== race.incumbentParty;

export const leftParty = (race: Race): Party => race.left?.party ?? "D";

/* ── Senate odds ───────────────────────────────── */

/**
 * Monte Carlo over the races with a shared national error (polls miss
 * together) plus race-specific noise. Control: Democrats need 51 seats
 * (the Vice President breaks ties for Republicans), Republicans 50.
 */
export const simulateSenate = (
  inputs: { forecast: RaceForecast; leftParty: Party }[],
  holdovers: Record<Party, number>
): SeatProjection => {
  const random = mulberry32(2026);
  const expected: Record<Party, number> = { ...holdovers };
  let demControl = 0;
  let repControl = 0;

  const races = inputs.map(({ forecast, leftParty: party }) => {
    const margin =
      forecast.margin ??
      forecast.sigma * normalQuantile(forecast.leftProbability);
    const national = Math.min(NATIONAL_ERROR, forecast.sigma);
    return {
      local: Math.sqrt(Math.max(forecast.sigma ** 2 - national ** 2, 1)),
      margin,
      national,
      party,
    };
  });

  for (const { forecast, leftParty: party } of inputs) {
    expected[party] += forecast.leftProbability;
    expected.R += 1 - forecast.leftProbability;
  }

  for (let i = 0; i < SIMULATIONS; i++) {
    const shift = gaussian(random);
    const seats: Record<Party, number> = { ...holdovers };
    for (const race of races) {
      const outcome =
        race.margin + shift * race.national + gaussian(random) * race.local;
      if (outcome > 0) seats[race.party]++;
      else seats.R++;
    }
    if (seats.D >= 51) demControl++;
    else if (seats.R >= 50) repControl++;
  }

  return {
    expected,
    priors: inputs.filter((x) => x.forecast.source === "prior").length,
    probabilities: {
      demControl: demControl / SIMULATIONS,
      independentsDecide: (SIMULATIONS - demControl - repControl) / SIMULATIONS,
      repControl: repControl / SIMULATIONS,
    },
  };
};

/* ── House districts ───────────────────────────── */

/** Change vs 2024 measured in a state, applied to its districts. */
export interface StateSwing {
  /** County FIPS → respondents and their unshrunk left-share change vs
      2024, for counties with respondents. */
  counties: Record<string, { n: number; swing: number }>;
  /** Error of the state read, in margin points. */
  sigma: number;
  /** Left-share change vs Harris's 2024 two-party share, 0–1. */
  swing: number;
}

export interface SeatInput {
  /** Party winning whatever happens; the forecast is then null. */
  decided: Party | null;
  forecast: RaceForecast | null;
  leftParty: Party;
  rightParty: Party;
  state: string;
  /** Error shared with the state's other districts (margin points). */
  stateSigma: number;
}

/** The party that wins a district whatever happens: one-party finalists
    (top-two) or an unopposed candidate. */
export const decidedParty = (district: District): Party | null => {
  const { left, right } = district;
  if (!left || !right) return (left ?? right)?.party ?? null;
  return left.party === right.party ? left.party : null;
};

/** Swing vs 2024 in a surveyed state, statewide and by county, from its
    House (generic ballot) read. */
export const measureSwing = (
  sample: StateSample,
  context: EstimateContext,
  estimate: ForesightEstimate
): StateSwing => {
  const counties: StateSwing["counties"] = {};
  for (const county of estimateCounties(sample, "house", context, estimate)) {
    const past = context.county2024?.[county.fips];
    if (past !== undefined && county.raw !== null) {
      counties[county.fips] = { n: county.n, swing: county.raw - past };
    }
  }
  return {
    counties,
    sigma: estimate.sigma,
    swing: estimate.leftShare - (1 - trumpTwoParty(context.state)),
  };
};

/** Surveyed states' swing weighted by their 2024 vote: the read for
    states not surveyed yet, with extra spread. */
export const nationalSwing = (
  reads: { state: StateInfo; swing: StateSwing }[]
): StateSwing | null => {
  if (reads.length === 0) return null;
  let swing = 0;
  let variance = 0;
  let total = 0;
  for (const read of reads) {
    const votes = read.state.pres2024.total;
    swing += read.swing.swing * votes;
    variance += read.swing.sigma ** 2 * votes;
    total += votes;
  }
  return {
    counties: {},
    sigma: Math.sqrt(variance / total + UNSURVEYED_ERROR ** 2),
    swing: swing / total,
  };
};

/**
 * A contested district: its 2024 result moved by the state swing plus the
 * district's own deviation, read from the respondents of the counties it
 * covers (weighted by their share of its vote) and shrunk toward zero
 * until they are many (DISTRICT_PRIOR_N). Only the covered share of the
 * district counts; the rest takes the state swing.
 */
export const projectDistrict = (
  district: District,
  swing: StateSwing,
  basis: DistrictProjection["basis"]
): DistrictProjection => {
  let total = 0;
  let covered = 0;
  let deviation = 0;
  let inverseN = 0;
  for (const [fips, weight] of district.counties) {
    total += weight;
    const county = swing.counties[fips];
    if (!county) continue;
    covered += weight;
    deviation += weight * (county.swing - swing.swing);
    inverseN += weight ** 2 / county.n;
  }
  const respondents = covered > 0 ? covered ** 2 / inverseN : 0;
  const trust =
    covered > 0
      ? (covered / total) * (respondents / (respondents + DISTRICT_PRIOR_N))
      : 0;
  const applied =
    swing.swing + (covered > 0 ? trust * (deviation / covered) : 0);
  const leftShare = clamp(district.harris2024 + applied, 0.01, 0.99);
  const margin = (2 * leftShare - 1) * 100;
  const sigma = Math.sqrt(
    swing.sigma ** 2 + DISTRICT_ERROR ** 2 * (1 - trust) + CANDIDATE_ERROR ** 2
  );
  return {
    basis,
    leftProbability: normalCdf(margin / sigma),
    leftShare,
    margin,
    respondents,
    sigma,
    stateSigma: swing.sigma,
    swing: applied,
  };
};

/**
 * Monte Carlo over the 435 seats: a national error (polls miss together),
 * a state error shared by its districts (one survey reads them all), then
 * each district's own noise. Control takes 218 seats.
 */
export const simulateHouse = (inputs: SeatInput[]): SeatProjection => {
  const random = mulberry32(2026);
  const fixed: Record<Party, number> = { D: 0, I: 0, R: 0 };
  const expected: Record<Party, number> = { D: 0, I: 0, R: 0 };
  const stateIndex = new Map<string, number>();
  let priors = 0;

  const races = inputs.flatMap((input) => {
    if (input.decided || !input.forecast) {
      if (input.decided) {
        fixed[input.decided]++;
        expected[input.decided]++;
      }
      return [];
    }
    const { forecast } = input;
    if (forecast.source === "prior") priors++;
    expected[input.leftParty] += forecast.leftProbability;
    expected[input.rightParty] += 1 - forecast.leftProbability;
    if (!stateIndex.has(input.state))
      stateIndex.set(input.state, stateIndex.size);
    const national = Math.min(NATIONAL_ERROR, forecast.sigma);
    const state = Math.sqrt(
      Math.max(
        Math.min(input.stateSigma, forecast.sigma) ** 2 - national ** 2,
        0
      )
    );
    return [
      {
        left: input.leftParty,
        local: Math.sqrt(
          Math.max(forecast.sigma ** 2 - national ** 2 - state ** 2, 1)
        ),
        margin:
          forecast.margin ??
          forecast.sigma * normalQuantile(forecast.leftProbability),
        national,
        right: input.rightParty,
        state,
        stateAt: stateIndex.get(input.state) ?? 0,
      },
    ];
  });

  const stateShifts = new Array<number>(stateIndex.size).fill(0);
  let demControl = 0;
  let repControl = 0;
  for (let i = 0; i < HOUSE_SIMULATIONS; i++) {
    const shift = gaussian(random);
    for (let s = 0; s < stateShifts.length; s++)
      stateShifts[s] = gaussian(random);
    const seats: Record<Party, number> = { ...fixed };
    for (const race of races) {
      const outcome =
        race.margin +
        shift * race.national +
        stateShifts[race.stateAt] * race.state +
        gaussian(random) * race.local;
      seats[outcome > 0 ? race.left : race.right]++;
    }
    if (seats.D >= HOUSE_MAJORITY) demControl++;
    else if (seats.R >= HOUSE_MAJORITY) repControl++;
  }

  return {
    expected,
    priors,
    probabilities: {
      demControl: demControl / HOUSE_SIMULATIONS,
      independentsDecide:
        (HOUSE_SIMULATIONS - demControl - repControl) / HOUSE_SIMULATIONS,
      repControl: repControl / HOUSE_SIMULATIONS,
    },
  };
};
